import React, { useEffect, useState } from 'react';
import axiosInstance from '../../api/axiosConfig';
import {
    Container,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Button,
    Snackbar,
    Alert,
    TextField,
    Card,
    CardContent,
    IconButton,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const LeaveCategoryManager = () => {
    const [leaveCategories, setLeaveCategories] = useState([]);
    const [newLeaveCategory, setNewLeaveCategory] = useState({
        typeName: '',
        isPaid: true,
        numberAvailable: 0,
    });
    const [editingLeaveCategory, setEditingLeaveCategory] = useState(null);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('success');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    // Fetch leave categories from the server
    const fetchLeaveCategories = async () => {
        try {
            const response = await axiosInstance.get('/api/leave-categories');
            setLeaveCategories(response.data);
        } catch (error) {
            console.error('Error fetching leave categories:', error);
            const message = error.response?.data || 'Error fetching leave categories';
            setAlertMessage(message);
            setAlertSeverity('error');
            setOpenSnackbar(true);
        }
    };

    useEffect(() => {
        fetchLeaveCategories();
    }, []);

    // Handle form submission for adding or editing leave categories
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!newLeaveCategory.typeName.trim()) {
            setAlertMessage('Category name is required');
            setAlertSeverity('error');
            setOpenSnackbar(true);
            return;
        }

        if (newLeaveCategory.numberAvailable < 0) {
            setAlertMessage('Number of available leaves cannot be negative');
            setAlertSeverity('error');
            setOpenSnackbar(true);
            return;
        }

        try {
            const payload = {
                typeName: newLeaveCategory.typeName.trim(),
                isPaid: newLeaveCategory.isPaid,
                numberAvailable: newLeaveCategory.numberAvailable,
            };

            if (editingLeaveCategory) {
                // Update existing leave category
                await axiosInstance.put(`/api/leave-categories/${editingLeaveCategory.leaveTypeId}`, payload);
                setAlertMessage('Leave category updated successfully!');
            } else {
                // Create new leave category
                await axiosInstance.post('/api/leave-categories', payload);
                setAlertMessage('Leave category added successfully!');
            }
            setAlertSeverity('success');
            setOpenSnackbar(true);
            fetchLeaveCategories(); // Refresh the list
            resetForm();
        } catch (error) {
            console.error('Error saving leave category:', error);
            const message = error.response?.data || 'Error saving leave category';
            setAlertMessage(message);
            setAlertSeverity('error');
            setOpenSnackbar(true);
        }
    };

    // Handle edit
    const handleEdit = (leaveCategory) => {
        setNewLeaveCategory({
            typeName: leaveCategory.typeName,
            isPaid: leaveCategory.isPaid,
            numberAvailable: leaveCategory.numberAvailable,
        });
        setEditingLeaveCategory(leaveCategory);
    };

    // Handle delete
    const handleDeleteClick = (leaveTypeId) => {
        setCategoryToDelete(leaveTypeId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axiosInstance.delete(`/api/leave-categories/${categoryToDelete}`);
            setAlertMessage('Leave category deleted successfully!');
            setAlertSeverity('success');
            setOpenSnackbar(true);
            fetchLeaveCategories(); // Refresh the list
        } catch (error) {
            console.error('Error deleting leave category:', error);
            const message = error.response?.data || 'Error deleting leave category';
            setAlertMessage(message);
            setAlertSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setDeleteDialogOpen(false);
            setCategoryToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setCategoryToDelete(null);
    };

    // Reset form
    const resetForm = () => {
        setNewLeaveCategory({ typeName: '', isPaid: true, numberAvailable: 0 });
        setEditingLeaveCategory(null);
    };

    const handleSnackbarClose = () => {
        setOpenSnackbar(false);
    };

    return (
        <Container>
            <Typography variant="h4" align="center" sx={{ marginBottom: 4 }}>
                Leave Category Management
            </Typography>

            <Card sx={{ marginBottom: 4 }}>
                <CardContent>
                    <Typography variant="h6">
                        {editingLeaveCategory ? 'Edit Leave Category' : 'Add Leave Category'}
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            label="Category Name"
                            variant="outlined"
                            fullWidth
                            value={newLeaveCategory.typeName}
                            onChange={(e) => setNewLeaveCategory({ ...newLeaveCategory, typeName: e.target.value })}
                            required
                            sx={{ marginBottom: 2 }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newLeaveCategory.isPaid}
                                    onChange={(e) => setNewLeaveCategory({ ...newLeaveCategory, isPaid: e.target.checked })}
                                />
                            }
                            label="Paid Leave"
                        />
                        <TextField
                            label="Number of Available Leaves"
                            variant="outlined"
                            type="number"
                            fullWidth
                            value={newLeaveCategory.numberAvailable}
                            onChange={(e) =>
                                setNewLeaveCategory({ ...newLeaveCategory, numberAvailable: parseInt(e.target.value) })
                            }
                            required
                            sx={{ marginTop: 2 }}
                        />
                        <Button variant="contained" color="primary" type="submit" sx={{ marginTop: 2 }}>
                            {editingLeaveCategory ? 'Update' : 'Add'} Leave Category
                        </Button>
                        {editingLeaveCategory && (
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={resetForm}
                                sx={{ marginTop: 2, marginLeft: 2 }}
                            >
                                Cancel
                            </Button>
                        )}
                    </form>
                </CardContent>
            </Card>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Category ID</TableCell>
                            <TableCell>Category Name</TableCell>
                            <TableCell>Is Paid</TableCell>
                            <TableCell>Number Available</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leaveCategories.map((leaveCategory) => (
                            <TableRow key={leaveCategory.leaveTypeId}>
                                <TableCell>{leaveCategory.leaveTypeId}</TableCell>
                                <TableCell>{leaveCategory.typeName}</TableCell>
                                <TableCell>{leaveCategory.isPaid ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{leaveCategory.numberAvailable}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleEdit(leaveCategory)} aria-label="edit">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleDeleteClick(leaveCategory.leaveTypeId)}
                                        aria-label="delete"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
                <DialogTitle>Delete Leave Category</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this leave category?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="secondary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for alerts */}
            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={alertSeverity} sx={{ width: '100%' }}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default LeaveCategoryManager;
